export interface TerminologyMap {
  deal: string;
  deals: string;
  contact: string;
  contacts: string;
  pipeline: string;
  pipelines: string;
  stage: string;
  task: string;
}

export type TerminologyKey = keyof TerminologyMap;

export const DEFAULT_TERMINOLOGY: TerminologyMap = {
  deal: "Deal",
  deals: "Deals",
  contact: "Contact",
  contacts: "Contacts",
  pipeline: "Pipeline",
  pipelines: "Pipelines",
  stage: "Stage",
  task: "Task",
};

export function resolveTerminologyMap(
  overrides?: Partial<TerminologyMap> | null,
): TerminologyMap {
  return {
    ...DEFAULT_TERMINOLOGY,
    ...(overrides ?? {}),
  };
}

// ---------------------------------------------------------------------------
// AG-019 — Persona-keyed language pack.
//
// A persona axis re-labels the same product surface depending on who is
// signed in (owner / agency / staff / client). The persona is multiplied by
// a `role` slug so e.g. `(agency, staff)` can re-label "deal" to "engagement"
// while `(client, viewer)` re-labels "deal" to "project".
//
// Resolution is a four-step fallback chain so packs never need to enumerate
// every (persona, role) tuple:
//
//   (persona, role) -> (persona, "*") -> ("*", role) -> DEFAULT_TERMINOLOGY
//
// Decision packet: docs/vision_execution/openclaw/AG-019-DECISION-PACKET.md
// ---------------------------------------------------------------------------

export const PERSONA_AXES = ["owner", "agency", "staff", "client"] as const;
export type PersonaAxis = (typeof PERSONA_AXES)[number];

/**
 * The wildcard slot for the `role` half of a pack key. A pack registered with
 * role `*` applies to every role within its persona unless a more specific
 * `(persona, role)` pack overrides it.
 */
export const PERSONA_ROLE_WILDCARD = "*";

/**
 * The wildcard slot for the `persona` half of a pack key. A pack registered
 * with persona `*` applies to every persona unless a more specific
 * `(persona, *)` or `(persona, role)` pack overrides it.
 */
export const PERSONA_AXIS_WILDCARD = "*";

export interface PersonaLanguagePackKey {
  readonly persona: PersonaAxis;
  readonly role: string;
}

export interface PersonaLanguagePack {
  readonly persona: PersonaAxis | typeof PERSONA_AXIS_WILDCARD;
  readonly role: string;
  readonly terminology: Partial<TerminologyMap>;
}

export type PersonaLanguagePackRegistry = ReadonlyArray<PersonaLanguagePack>;

function packKey(
  persona: PersonaAxis | typeof PERSONA_AXIS_WILDCARD,
  role: string,
): string {
  return `${persona}::${role}`;
}

function indexRegistry(
  registry: PersonaLanguagePackRegistry,
): Map<string, Partial<TerminologyMap>> {
  const index = new Map<string, Partial<TerminologyMap>>();
  for (const pack of registry) {
    index.set(packKey(pack.persona, pack.role), pack.terminology);
  }
  return index;
}

/**
 * Resolves the terminology map for a `(persona, role)` tuple by merging the
 * four fallback layers in precedence order:
 *
 *   1. exact `(persona, role)` pack
 *   2. `(persona, "*")` pack — persona-wide override
 *   3. `("*", role)` pack — role-wide override (cross-persona)
 *   4. `DEFAULT_TERMINOLOGY`
 *
 * Earlier layers WIN per-key over later layers, so a `(agency, staff)` pack
 * setting `deal: "Engagement"` keeps "Engagement" even if `(agency, "*")`
 * separately maps `deal: "Project"`. `DEFAULT_TERMINOLOGY` is treated as
 * frozen — callers receive a fresh object every call.
 */
export function resolvePersonaTerminology(
  registry: PersonaLanguagePackRegistry,
  key: PersonaLanguagePackKey,
): TerminologyMap {
  const index = indexRegistry(registry);

  const exact = index.get(packKey(key.persona, key.role));
  const personaWild = index.get(packKey(key.persona, PERSONA_ROLE_WILDCARD));
  const roleWild = index.get(packKey(PERSONA_AXIS_WILDCARD, key.role));

  return {
    ...DEFAULT_TERMINOLOGY,
    ...(roleWild ?? {}),
    ...(personaWild ?? {}),
    ...(exact ?? {}),
  };
}

export function isPersonaAxis(value: unknown): value is PersonaAxis {
  return typeof value === "string" && (PERSONA_AXES as readonly string[]).includes(value);
}
